import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Copy, Check, Terminal, Zap, AlertTriangle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

interface MissionStats {
  breakdown: Record<string, Record<string, number>>;
  pending: number;
  limits: Record<string, number>;
  executed_today: Record<string, number>;
  days: number;
}

interface Incident {
  id: string;
  severity: string;
  code: string;
  message: string;
  created_at: string;
  notified: boolean;
}

export default function CoworkPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [installCmd, setInstallCmd] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState<MissionStats | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);

  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [loading, user, navigate]);

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const [s, i] = await Promise.all([
        fetch(`${BACKEND}/api/cowork/missions/stats`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)),
        fetch(`${BACKEND}/api/cowork/incidents/recent?limit=5`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)),
      ]);
      if (s) setStats(s);
      if (i) setIncidents(i.incidents || []);
    } catch {
      /* noop */
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    if (user) loadStats();
  }, [user]);

  const generateInstall = async () => {
    setGenerating(true);
    try {
      const r = await fetch(`${BACKEND}/api/cowork/install/generate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!r.ok) throw new Error("Génération impossible");
      const data = await r.json();
      setInstallCmd(data.command);
      toast.success("Commande d'installation prête. Valable 24h.");
    } catch (e) {
      toast.error("Erreur : impossible de générer la commande");
    } finally {
      setGenerating(false);
    }
  };

  const copyCmd = async () => {
    try {
      await navigator.clipboard.writeText(installCmd);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = installCmd;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Commande copiée");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return null;

  const totalSent = stats ? Object.values(stats.breakdown).reduce((acc, v) => acc + (v.sent || 0), 0) : 0;
  const totalReplied = stats ? Object.values(stats.breakdown).reduce((acc, v) => acc + (v.replied || 0), 0) : 0;
  const totalAccepted = stats ? Object.values(stats.breakdown).reduce((acc, v) => acc + (v.accepted || 0), 0) : 0;

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 space-y-6 pb-12" data-testid="cowork-page">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="cowork-title">
                <Zap className="h-7 w-7 text-primary" /> Claude Cowork
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Automatisation LinkedIn exécutée sur votre ordinateur, pilotée par Grafton.
              </p>
            </div>

            {/* Installation one-liner */}
            <Card data-testid="cowork-install-card" className="border-primary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Terminal className="h-5 w-5 text-primary" /> Installation en une ligne
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Cliquez pour générer votre commande personnalisée. Collez-la dans le Terminal de votre ordinateur :
                  le service s'installera et démarrera automatiquement.
                </p>

                {!installCmd ? (
                  <Button
                    onClick={generateInstall}
                    disabled={generating}
                    data-testid="generate-install-btn"
                    size="lg"
                    className="gap-2"
                  >
                    {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                    Générer ma commande d'installation
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <div
                      className="relative bg-black/70 rounded-lg p-4 font-mono text-xs text-emerald-300 overflow-x-auto"
                      data-testid="install-command"
                    >
                      <span className="select-all break-all">{installCmd}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button onClick={copyCmd} variant="secondary" size="sm" data-testid="copy-install-btn">
                        {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                        {copied ? "Copié" : "Copier"}
                      </Button>
                      <Button onClick={generateInstall} variant="ghost" size="sm" disabled={generating}>
                        Régénérer
                      </Button>
                      <span className="text-xs text-muted-foreground ml-auto">Valable 24h · usage unique</span>
                    </div>
                  </div>
                )}

                <div className="rounded border border-border/50 bg-muted/30 p-3 text-xs text-muted-foreground space-y-1">
                  <div className="font-semibold text-foreground">Ensuite, côté Claude Cowork :</div>
                  <div>
                    Configurez Cowork pour surveiller le dossier <code className="px-1 bg-background rounded">~/cowork/grafton_missions</code>.
                  </div>
                  <div>
                    Pour chaque <code className="px-1 bg-background rounded">.json</code> qui apparaît dans ce dossier, Cowork exécute la mission LinkedIn puis ajoute <code className="px-1 bg-background rounded">{'{ "result": { "status": "sent" } }'}</code> dans le même fichier.
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card data-testid="stat-pending">
                <CardContent className="pt-5">
                  <div className="text-xs text-muted-foreground">En attente</div>
                  <div className="text-2xl font-semibold">{loadingStats ? "—" : stats?.pending ?? 0}</div>
                </CardContent>
              </Card>
              <Card data-testid="stat-sent">
                <CardContent className="pt-5">
                  <div className="text-xs text-muted-foreground">Envoyés (7j)</div>
                  <div className="text-2xl font-semibold text-sky-400">{loadingStats ? "—" : totalSent}</div>
                </CardContent>
              </Card>
              <Card data-testid="stat-accepted">
                <CardContent className="pt-5">
                  <div className="text-xs text-muted-foreground">Acceptés</div>
                  <div className="text-2xl font-semibold text-emerald-400">{loadingStats ? "—" : totalAccepted}</div>
                </CardContent>
              </Card>
              <Card data-testid="stat-replied">
                <CardContent className="pt-5">
                  <div className="text-xs text-muted-foreground">Répondus</div>
                  <div className="text-2xl font-semibold text-amber-400">{loadingStats ? "—" : totalReplied}</div>
                </CardContent>
              </Card>
            </div>

            {/* Quotas today */}
            {stats && (
              <Card data-testid="cowork-quotas-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Send className="h-5 w-5 text-primary" /> Quotas du jour
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.keys(stats.limits).map((k) => {
                      const used = stats.executed_today[k] ?? 0;
                      const cap = stats.limits[k];
                      const pct = Math.min(100, Math.round((used / Math.max(cap, 1)) * 100));
                      return (
                        <div key={k}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-muted-foreground">{k.replace("linkedin_", "")}</span>
                            <span className="font-mono">
                              {used} / {cap}
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full ${pct > 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Incidents */}
            <Card data-testid="cowork-incidents-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-400" /> Incidents techniques (5 derniers)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {incidents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucun incident signalé.</p>
                ) : (
                  <div className="space-y-2">
                    {incidents.map((inc) => {
                      const sevColor =
                        inc.severity === "critical" || inc.severity === "error"
                          ? "destructive"
                          : inc.severity === "warning"
                            ? "default"
                            : "secondary";
                      return (
                        <div
                          key={inc.id}
                          className="flex items-start gap-3 text-sm border-b border-border/40 pb-2 last:border-0"
                        >
                          <Badge variant={sevColor as any} className="shrink-0">
                            {inc.severity.toUpperCase()}
                          </Badge>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium">{inc.code || "—"}</div>
                            <div className="text-xs text-muted-foreground break-words">{inc.message}</div>
                          </div>
                          <div className="text-xs text-muted-foreground shrink-0">
                            {new Date(inc.created_at).toLocaleString("fr-FR")}
                            {inc.notified && <span className="ml-2 text-emerald-400">✓ notifié</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
